import React, { useEffect } from 'react'
import gsap from 'gsap'

interface UsePulseAnimationProps {
    ref: React.RefObject<HTMLElement | null>
    pulse?: boolean
    // Extra properties animated alongside the base scale/filter pulse (e.g. a boxShadow glow at peak
    // scale). Merged into the peak ("on") and rest ("off"/cleanup) tweens respectively — provide a
    // matching pair (e.g. the same shadow color at full vs zero alpha/spread) so both interpolate
    // smoothly instead of jumping, the same reason the base scale/filter have explicit rest values.
    peakStyles?: gsap.TweenVars
    restStyles?: gsap.TweenVars
}

// Attention-grabbing scale + brightness loop (e.g. "click me") for a button. Starts/stops — with a
// smooth settle back to rest — as `pulse` toggles.
const usePulseAnimation = ({ ref, pulse, peakStyles, restStyles }: UsePulseAnimationProps) => {
    useEffect(() => {
        const el = ref.current

        if (!el) return

        if (pulse) {
            // Explicit rest-state baseline: without it, a button pulsing from the moment it first
            // mounts (never yet settled at rest, unlike an always-mounted button whose pulse toggles on
            // later) has no prior "brightness(1)" to interpolate from, and GSAP jumps straight from the
            // browser's implicit `filter: none` instead of easing in — a visible flash on that first cycle.
            gsap.set(el, { scale: 1, filter: 'brightness(1)', ...restStyles })

            const tween = gsap.to(el, {
                scale: 1.1,
                duration: 0.5,
                filter: 'brightness(1.25)',
                ease: 'power2.inOut',
                repeat: -1,
                yoyo: true,
                ...peakStyles,
            })

            return () => {
                tween.kill()
                gsap.to(el, {
                    scale: 1,
                    filter: 'brightness(1)',
                    duration: 0.3,
                    ease: 'power2.out',
                    ...restStyles,
                })
            }
        }
    }, [pulse])
}

export default usePulseAnimation
